import { Flag } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DeskInfo, DispatchOrder } from "../../types/Dispatch";
import DispatchSessionBar from "./dispatch-session-bar";
import { claimNotice } from "./deskOrderState";
import "../../styles/dispatch-bench.css";

type Props = {
    deskInfo: DeskInfo;
    toteLabel?: string;
    // The orders still in the open tote — everything the item scan has not
    // opened yet. Orders open at another desk are listed but not flagged.
    orders: DispatchOrder[];
    busy: boolean;
    onCancel: () => void;
    onConfirm: (orders: DispatchOrder[]) => void;
};

/** Orders the confirm flags: every order in the tote nobody else holds. */
export const flaggableOrders = (orders: DispatchOrder[]): DispatchOrder[] =>
    orders.filter(o => !o.claimed_by);

/**
 * Singles tote › Flag missing (2026-10-28). On a Single tote the item scan
 * is the only way into an order, so an order whose unit is not in the tote
 * could never be opened — and so never flagged — and sat on the tote until
 * the unit turned up. This lists what is left and flags every one as missing
 * at dispatch, for a team leader on Dispatch › Flagged.
 */
const DispatchFlagRemaining = ({ deskInfo, toteLabel, orders, busy, onCancel, onConfirm }: Props) => {
    const { t } = useTranslation();
    const toFlag = flaggableOrders(orders);

    return (
        <div className="dispatch-page dispatch-bench dispatch-flag-screen">
            <DispatchSessionBar deskId={deskInfo.deskId} toteLabel={toteLabel} onBack={onCancel} />
            <div className="dispatch-flag-stage">
                <div className="dispatch-flag-confirm-card">
                    <div className="dispatch-flag-confirm-head">
                        <div className="dispatch-flag-confirm-icon">
                            <Flag size={28} aria-hidden="true" />
                        </div>
                        <h2 className="dispatch-flag-confirm-title">{t("flag_remaining_title")}</h2>
                        <p className="dispatch-flag-confirm-reason">{t("flagged_reason_missing_dispatch")}</p>
                    </div>

                    <ul className="dispatch-flag-remaining-list">
                        {orders.map(o => (
                            <li
                                key={o.order_id}
                                className={`dispatch-flag-remaining-row${o.claimed_by ? " dispatch-flag-remaining-row--claimed" : ""}`}
                            >
                                {o.items[0]?.image_url
                                    ? <img className="dispatch-flag-remaining-img" src={o.items[0].image_url} alt="" />
                                    : <span className="dispatch-flag-remaining-img" aria-hidden="true" />}
                                <div className="dispatch-flag-remaining-text">
                                    <span className="dispatch-flag-remaining-title">
                                        {o.items.map(i => i.title || i.sku).join(", ")}
                                    </span>
                                    <span className="dispatch-flag-remaining-meta">
                                        {o.items.map(i => `${i.sku} × ${i.quantity}`).join(", ")} · {o.order_number}
                                    </span>
                                    {o.claimed_by && (
                                        <span className="dispatch-flag-remaining-claimed">
                                            {claimNotice(t, o.claimed_by)}
                                        </span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    <p className="dispatch-flag-confirm-hint">{t("flag_remaining_hint")}</p>

                    <div className="dispatch-flag-confirm-actions">
                        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => onConfirm(toFlag)}
                            disabled={busy || toFlag.length === 0}
                        >
                            <Flag size={22} aria-hidden="true" />
                            {busy ? t("loading") : t("flag_remaining_confirm", { count: String(toFlag.length) })}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DispatchFlagRemaining;
